import { Calculator } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { CouponPicker } from '@/features/coupons';
import { GlobalDiscountPicker } from '@/features/global-discount';
import { countryName, useSelectedCountry } from '@/features/country-scope';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';

import { usePricingCalculation } from '../api/pricing.queries';
import { CalculationInputs } from '../components/calculation-inputs';
import {
  CalculationSteps,
  CalculationSummary,
  CalculationUnavailable,
  FinalPriceCard,
  FullFormula,
  ProviderDecision,
  SimulationBanner,
} from '../components/calculation-result';
import { CouponResult, GlobalDiscountResult } from '../components/coupon-result';
import { EMPTY_SELECTION, type CalculationSelection } from '../components/calculation-selection';
import type { PricingCalculationRequest, PricingProviderCandidate } from '../types/pricing.types';

const NO_PROVIDERS: PricingProviderCandidate[] = [];

function positive(value: string | null): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

/** The selection lives in the URL (shareable, survives back-navigation); `c` ties it to a country. */
function readSelection(params: URLSearchParams, countryId: number | null): CalculationSelection {
  if (countryId === null || positive(params.get('c')) !== countryId) return EMPTY_SELECTION;
  return {
    brandId: positive(params.get('brand')),
    regionId: positive(params.get('region')),
    denominationId: positive(params.get('item')),
    paymentChannelCode: params.get('channel') || null,
    providerId: positive(params.get('provider')),
  };
}

/**
 * What to apply after the engine: an optional coupon (and the customer to check it as), and which
 * global discount — null = the country's running sale, exactly as checkout.
 */
interface CouponSelection {
  couponId: number | null;
  customerId: number | null;
  globalDiscountId: number | null;
}

function toRequest(
  countryId: number | null,
  s: CalculationSelection,
  coupon: CouponSelection,
): PricingCalculationRequest | null {
  if (!countryId || !s.brandId || !s.regionId || !s.denominationId || !s.paymentChannelCode)
    return null;
  return {
    countryId,
    brandId: s.brandId,
    regionId: s.regionId,
    denominationId: s.denominationId,
    paymentChannelCode: s.paymentChannelCode,
    ...(s.providerId ? { providerId: s.providerId } : {}),
    ...(coupon.couponId ? { couponId: coupon.couponId } : {}),
    ...(coupon.couponId && coupon.customerId ? { customerId: coupon.customerId } : {}),
    ...(coupon.globalDiscountId ? { globalDiscountId: coupon.globalDiscountId } : {}),
  };
}

/**
 * Pricing Calculation Inspector: "what would checkout charge now, and why?"
 *
 * The backend runs the checkout pricing engine itself (no separate formula) and
 * returns every step; this page chooses the inputs and displays the answer. It
 * never calculates money. It is not the historical Transaction Pricing Snapshot,
 * which explains how a past order was priced.
 */
export function PricingCalculatorPage() {
  const { country } = useSelectedCountry();
  const countryId = country?.id ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = readSelection(searchParams, countryId);
  const brandSearch = searchParams.get('q') ?? '';
  const { can } = usePermissions();
  const canUseCoupons = can('coupons.read');
  const sameCountry = countryId !== null && positive(searchParams.get('c')) === countryId;
  const coupon: CouponSelection = {
    couponId: canUseCoupons && sameCountry ? positive(searchParams.get('coupon')) : null,
    customerId: canUseCoupons && sameCountry ? positive(searchParams.get('customer')) : null,
    globalDiscountId: canUseCoupons && sameCountry ? positive(searchParams.get('sale')) : null,
  };

  const update = (next: CalculationSelection, search = brandSearch, nextCoupon = coupon) => {
    const params = new URLSearchParams();
    if (countryId !== null) params.set('c', String(countryId));
    if (search) params.set('q', search);
    if (next.brandId) params.set('brand', String(next.brandId));
    if (next.regionId) params.set('region', String(next.regionId));
    if (next.denominationId) params.set('item', String(next.denominationId));
    if (next.paymentChannelCode) params.set('channel', next.paymentChannelCode);
    if (next.providerId) params.set('provider', String(next.providerId));
    if (nextCoupon.couponId) params.set('coupon', String(nextCoupon.couponId));
    if (nextCoupon.couponId && nextCoupon.customerId)
      params.set('customer', String(nextCoupon.customerId));
    if (nextCoupon.globalDiscountId) params.set('sale', String(nextCoupon.globalDiscountId));
    setSearchParams(params, { replace: true });
  };

  const request = toRequest(countryId, selection, coupon);
  const calculation = usePricingCalculation(request);
  const data = calculation.data;

  // The provider choice offers the candidates the engine evaluated for this item.
  const itemKey = `${countryId}/${selection.denominationId}/${selection.paymentChannelCode}`;
  const [providers, setProviders] = useState({ key: '', list: NO_PROVIDERS });
  if (data?.provider && providers.key !== itemKey && calculation.isSuccess) {
    setProviders({ key: itemKey, list: data.provider.candidates });
  }
  const providerOptions = providers.key === itemKey ? providers.list : NO_PROVIDERS;

  return (
    <PageContainer>
      <PageHeader
        title="Pricing Calculator"
        description="How checkout prices an item right now: the same pricing engine with the current provider costs, exchange rates, pricing configuration and payment fees. Read-only; each calculation is recorded in the audit log."
      />

      {country === null ? (
        <EmptyState
          icon={Calculator}
          title="No country in your scope"
          description="Pricing is calculated per country; ask an administrator for access to one."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <CalculationInputs
            countryId={country.id}
            countryLabel={countryName(country)}
            value={selection}
            onChange={(next) => update(next)}
            onReset={() =>
              update(EMPTY_SELECTION, '', {
                couponId: null,
                customerId: null,
                globalDiscountId: null,
              })
            }
            brandSearch={brandSearch}
            onBrandSearchChange={(search) => update(selection, search)}
            providers={providerOptions}
          />

          {canUseCoupons && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <CouponPicker
                  countryId={country.id}
                  couponId={coupon.couponId}
                  customerId={coupon.customerId}
                  onChange={(next) => update(selection, brandSearch, { ...coupon, ...next })}
                />
              </div>
              <GlobalDiscountPicker
                countryId={country.id}
                value={coupon.globalDiscountId}
                onChange={(globalDiscountId) =>
                  update(selection, brandSearch, { ...coupon, globalDiscountId })
                }
              />
            </div>
          )}

          {!request && (
            <EmptyState
              icon={Calculator}
              title="Choose what to price"
              description="Select a brand, store/region, denomination and payment channel. The provider defaults to Auto — exactly what checkout does."
            />
          )}

          {request && calculation.isLoading && <ResultSkeleton />}
          {request && calculation.error && (
            <ErrorState error={calculation.error} onRetry={() => void calculation.refetch()} />
          )}

          {request && data && (
            <div className="flex flex-col gap-4">
              <SimulationBanner data={data} />
              {data.outcome === 'Priced' ? (
                <>
                  <FinalPriceCard data={data} />
                  {data.globalDiscount && (
                    <GlobalDiscountResult
                      discount={data.globalDiscount}
                      before={data.result ?? null}
                    />
                  )}
                  {data.coupon && (
                    <CouponResult coupon={data.coupon} before={data.result ?? null} />
                  )}
                  <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[2fr_1fr]">
                    <CalculationSteps
                      steps={[
                        ...data.steps,
                        ...(data.globalDiscount?.step ? [data.globalDiscount.step] : []),
                        ...(data.coupon?.step ? [data.coupon.step] : []),
                      ]}
                    />
                    <CalculationSummary data={data} />
                  </div>
                  <FullFormula data={data} />
                </>
              ) : (
                <CalculationUnavailable data={data} />
              )}
              {data.provider && <ProviderDecision decision={data.provider} />}
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}

/** The shape of the answer while the engine works, so the page does not jump. */
function ResultSkeleton() {
  return (
    <div role="status" aria-label="Calculating" className="flex flex-col gap-4">
      <Skeleton className="h-28" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
        <Skeleton className="h-52" />
      </div>
    </div>
  );
}
